// First-party heatmap client — aggregate only.
// Behaviour:
//  * Instruments the CURRENT page only when it is in the ALLOWED_PAGES set.
//  * Skips completely for Growing Circle / admin / verification / auth routes.
//  * Captures:
//      - click (x_pct, y_pct_page, nearest [data-section], nearest [data-cta])
//      - max scroll depth (10% granularity, reported once at flush)
//      - per-section time-in-view via IntersectionObserver ≥ 50% visibility
//        (paused while document.hidden — no phantom time from abandoned tabs)
//      - active page duration (paused while document.hidden, capped 30 min)
//  * Batches everything into one `sendBeacon` on visibilitychange:hidden
//    and on beforeunload. Also opportunistic 25s idle flush.
//  * Only fires when the server-supplied {enabled:true} flag is set
//    AND (if requires_measurement_consent) iubenda has stored consent for
//    purpose "4" (Measurement).
//
// No cookies, no localStorage, no fingerprint. All raw pixel/UA data stays
// inside the browser; only bucketable percentages leave the client.

const ALLOWED_PAGES = new Set(["/"]);

const state = {
  enabled: false,
  requiresConsent: true,
  page: "/",
  layoutVersion: null,
  viewportBucket: "desktop",
  // Per-pageload accumulators
  clicks: [],
  sectionTimes: new Map(), // section_id -> { ms, reached, obs, lastEnter }
  reachedSections: new Set(),
  maxScrollPct: 0,
  pageStartedAt: 0,
  pageActiveMs: 0,
  activeSince: 0,
  observers: [],
  idleTimer: null,
  flushing: false,
  installed: false,
};

const SECTION_TIME_CAP_MS = 60_000;
const PAGE_DURATION_CAP_MS = 30 * 60_000;
const REACH_MIN_TIME_MS = 1_000; // ≥ 1s at ≥ 50% visibility = "reached"

function computeViewport() {
  const w = window.innerWidth || document.documentElement.clientWidth || 0;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function pageScrollPct() {
  const doc = document.documentElement;
  const body = document.body || {};
  const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
  const viewH = window.innerHeight || doc.clientHeight || 0;
  const totalH = Math.max(
    doc.scrollHeight, doc.offsetHeight,
    body.scrollHeight || 0, body.offsetHeight || 0,
  );
  if (totalH <= viewH) return 100;
  return Math.min(100, ((scrollTop + viewH) / totalH) * 100);
}

function nearestSection(el) {
  let n = el;
  while (n && n.nodeType === 1) {
    if (n.getAttribute && n.getAttribute("data-section")) {
      return n.getAttribute("data-section");
    }
    n = n.parentNode;
  }
  return null;
}

function nearestCta(el) {
  let n = el;
  while (n && n.nodeType === 1) {
    if (n.getAttribute && n.getAttribute("data-cta")) {
      return n.getAttribute("data-cta");
    }
    n = n.parentNode;
  }
  return null;
}

function hasMeasurementConsent() {
  // iubenda writes the choice into `_iub.cs.consent.purposes`. If iubenda
  // has not yet emitted its consent state the value is undefined; in that
  // case we assume "no consent" (fail-closed).
  try {
    const cs = (window._iub && window._iub.cs && window._iub.cs.consent) || null;
    if (!cs) return false;
    const purposes = cs.purposes || cs.categories || {};
    // Purpose 4 = Measurement in iubenda's standard mapping.
    return purposes["4"] === true || purposes[4] === true;
  } catch {
    return false;
  }
}

function shouldRun() {
  if (!state.enabled) return false;
  if (!ALLOWED_PAGES.has(location.pathname)) return false;
  if (state.requiresConsent && !hasMeasurementConsent()) return false;
  return true;
}

// -------------------- Click capture --------------------
function onClick(evt) {
  if (!shouldRun()) return;
  const target = evt.target;
  if (!target || target.nodeType !== 1) return;
  // Compute page-relative percentages.
  const doc = document.documentElement;
  const body = document.body || {};
  const totalH = Math.max(
    doc.scrollHeight, doc.offsetHeight,
    body.scrollHeight || 0, body.offsetHeight || 0,
  );
  const totalW = Math.max(
    doc.scrollWidth, doc.offsetWidth,
    body.scrollWidth || 0, body.offsetWidth || 0,
  );
  const pageY = (window.pageYOffset || doc.scrollTop || 0) + (evt.clientY || 0);
  const xPct = totalW > 0 ? ((evt.clientX || 0) / (window.innerWidth || totalW)) * 100 : 0;
  const yPct = totalH > 0 ? (pageY / totalH) * 100 : 0;
  const section = nearestSection(target);
  const cta = nearestCta(target);
  state.clicks.push({
    section_id: section,
    x_pct: Math.max(0, Math.min(100, xPct)),
    y_pct_page: Math.max(0, Math.min(100, yPct)),
    cta_id: cta,
    is_amazon_outbound: false,
  });
  // Amazon outbound will be reported explicitly by BuyProvider through
  // `window.__sdHeatmapAmazonOutbound` — this generic listener does not
  // try to detect anchor hrefs.
  scheduleFlush();
}

// -------------------- Section visibility --------------------
function installSectionObservers() {
  // Every section carrying data-section on the current page.
  const nodes = document.querySelectorAll("[data-section]");
  nodes.forEach((el) => {
    const sid = el.getAttribute("data-section");
    if (!sid || state.sectionTimes.has(sid)) return;
    state.sectionTimes.set(sid, { ms: 0, lastEnter: 0, reached: false });
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const entry = state.sectionTimes.get(sid);
        if (!entry) return;
        if (e.isIntersecting && e.intersectionRatio >= 0.5 && !document.hidden) {
          entry.lastEnter = performance.now();
        } else if (entry.lastEnter) {
          const dt = performance.now() - entry.lastEnter;
          entry.lastEnter = 0;
          if (dt > 0) {
            entry.ms = Math.min(SECTION_TIME_CAP_MS, entry.ms + dt);
            if (entry.ms >= REACH_MIN_TIME_MS) entry.reached = true;
          }
        }
      });
    }, { threshold: [0.5] });
    obs.observe(el);
    state.observers.push(obs);
  });
}

function pauseSectionTiming() {
  const now = performance.now();
  state.sectionTimes.forEach((entry) => {
    if (entry.lastEnter) {
      const dt = now - entry.lastEnter;
      entry.lastEnter = 0;
      entry.ms = Math.min(SECTION_TIME_CAP_MS, entry.ms + dt);
      if (entry.ms >= REACH_MIN_TIME_MS) entry.reached = true;
    }
  });
}

function resumeSectionTiming() {
  // Re-arm sections that are currently ≥ 50% visible.
  const now = performance.now();
  document.querySelectorAll("[data-section]").forEach((el) => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
    if (rect.height > 0 && visible / rect.height >= 0.5) {
      const entry = state.sectionTimes.get(el.getAttribute("data-section"));
      if (entry) entry.lastEnter = now;
    }
  });
}

// -------------------- Page duration --------------------
function startActive() {
  if (!state.activeSince) state.activeSince = performance.now();
}

function pauseActive() {
  if (state.activeSince) {
    const dt = performance.now() - state.activeSince;
    state.pageActiveMs = Math.min(PAGE_DURATION_CAP_MS, state.pageActiveMs + dt);
    state.activeSince = 0;
  }
}

// -------------------- Scroll --------------------
function onScroll() {
  if (!shouldRun()) return;
  const pct = pageScrollPct();
  if (pct > state.maxScrollPct) state.maxScrollPct = pct;
}

// -------------------- Visibility --------------------
function onVisibility() {
  if (document.hidden) {
    pauseSectionTiming();
    pauseActive();
    flush(true);
  } else {
    startActive();
    resumeSectionTiming();
  }
}

// -------------------- Flush --------------------
function buildBody() {
  const sections = [];
  state.sectionTimes.forEach((entry, sid) => {
    if (entry.ms > 0 || entry.reached) {
      sections.push({
        section_id: sid,
        reached: !!entry.reached,
        time_in_view_ms: Math.max(0, Math.min(SECTION_TIME_CAP_MS, Math.round(entry.ms))),
      });
    }
  });
  return {
    page: state.page,
    layout_version: state.layoutVersion,
    viewport_bucket: state.viewportBucket,
    consent_measurement: !state.requiresConsent || hasMeasurementConsent(),
    clicks: state.clicks.slice(),
    sections,
    max_scroll_pct: state.maxScrollPct,
    page_duration_ms: Math.max(0, Math.min(PAGE_DURATION_CAP_MS, Math.round(state.pageActiveMs))),
  };
}

function resetAfterFlush() {
  state.clicks = [];
  state.sectionTimes.forEach((entry) => {
    // Keep the "reached" flag but zero the accumulated ms so the next
    // beacon doesn't double-count the same time-in-view.
    entry.ms = 0;
  });
  state.maxScrollPct = state.maxScrollPct; // keep max (monotonic)
  state.pageActiveMs = 0;
}

let BACKEND_URL = "";
export function configureBackendUrl(url) {
  BACKEND_URL = (url || "").replace(/\/+$/, "");
}

function flush(useBeacon) {
  if (!shouldRun()) return;
  if (state.flushing) return;
  const body = buildBody();
  const empty = body.clicks.length === 0 && body.sections.length === 0
                 && body.max_scroll_pct === 0 && body.page_duration_ms === 0;
  if (empty) return;
  state.flushing = true;
  try {
    const url = `${BACKEND_URL}/api/heatmap/ingest`;
    const payload = JSON.stringify(body);
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        credentials: "omit",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: payload,
      }).catch(() => {});
    }
    resetAfterFlush();
  } finally {
    state.flushing = false;
  }
}

function scheduleFlush() {
  if (state.idleTimer) return;
  state.idleTimer = setTimeout(() => {
    state.idleTimer = null;
    flush(false);
  }, 25_000);
}

// -------------------- Public API --------------------
export function reportAmazonOutbound({ marketplace, section, cta } = {}) {
  if (!shouldRun()) return;
  // Fire a single-click payload synchronously via sendBeacon so we don't
  // race the navigation to Amazon.
  const body = {
    page: state.page,
    layout_version: state.layoutVersion,
    viewport_bucket: state.viewportBucket,
    consent_measurement: !state.requiresConsent || hasMeasurementConsent(),
    clicks: [{
      section_id: section || null,
      x_pct: 0,
      y_pct_page: 0,
      cta_id: cta || "buy_click_amazon",
      is_amazon_outbound: true,
      marketplace: marketplace || null,
    }],
    sections: [],
    max_scroll_pct: state.maxScrollPct,
    page_duration_ms: 0,
  };
  const url = `${BACKEND_URL}/api/heatmap/ingest`;
  const payload = JSON.stringify(body);
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST", credentials: "omit", keepalive: true,
        headers: { "Content-Type": "application/json" }, body: payload,
      }).catch(() => {});
    }
  } catch {
    /* swallow */
  }
}

export async function install(backendUrl) {
  if (state.installed) return;
  state.installed = true;
  configureBackendUrl(backendUrl);
  state.page = location.pathname;
  if (!ALLOWED_PAGES.has(state.page)) return;
  state.viewportBucket = computeViewport();
  // Fetch layout gate.
  try {
    const r = await fetch(`${BACKEND_URL}/api/heatmap/layout?page=${encodeURIComponent(state.page)}`,
                           { credentials: "omit" });
    if (r.ok) {
      const j = await r.json();
      state.enabled = !!j.enabled;
      state.layoutVersion = j.layout_version || null;
      state.requiresConsent = j.requires_measurement_consent !== false;
    }
  } catch {
    state.enabled = false;
  }
  if (!state.enabled) return;
  // Expose the Amazon-outbound hook to non-module code (BuyProvider).
  window.__sdHeatmapAmazonOutbound = reportAmazonOutbound;

  state.pageStartedAt = performance.now();
  startActive();
  installSectionObservers();

  document.addEventListener("click", onClick, { passive: true, capture: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("resize", () => {
    state.viewportBucket = computeViewport();
  }, { passive: true });
  window.addEventListener("pagehide", () => flush(true));
  window.addEventListener("beforeunload", () => flush(true));

  // Also react when iubenda emits its consent event AFTER install: if the
  // gate flips, re-arm the observers so we don't miss the reached signal.
  document.addEventListener("iubenda_consent_given", () => {
    installSectionObservers();
    resumeSectionTiming();
  });
}
